import { Typography } from "@commonlibs";
import Excel from "../../../assets/icons/filled/Excel.svg";
import FileUploadArea, { FileUploadStatus } from "ui/common/components/input/FileUploadArea/FileUploadArea";
import { Fragment, useEffect, useState } from "react";
import { default as config } from "common/config/config";
import TranslatedText from "ui/common/components/text/TranslatedText/TranslatedText";
import UploadedFileItem from "ui/common/components/input/UploadedFileItem/UploadedFileItem";
import {
    ExplanationSection,
    FileUploadLoading,
    FileUploadSuccess,
    FileUploadError,
    CheckIconRoot,
    WarningIconRoot,
    ProgressBarRoot,
} from "ui/transaction/container/CustomerManagementModal/CustomerManagementModal.styles";
import useTranslate from "ui/locale/hooks/useTranslate";

interface FileUploaderProps {
    onLoadDocument: (file: File) => Promise<void> | void;
    onDeleteDocument?: () => void;
    fileUploadedStatus?: FileUploadStatus;
    explanationText?: string;
    linkText?: string;
    templateFileUrl?: string;
    showExplanationSection?: boolean;
    fileTypes?: string;
    /**
     * Ícono mostrado en el área de carga de archivos.
     *
     * Íconos disponibles actualmente en `ui/assets/icons/filled`:
     * - `Excel.svg` (valor por defecto)
     * - `InfoYellow.svg`
     * - `googleLogo.svg`
     */
    fileIcon?: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({
    onLoadDocument,
    onDeleteDocument,
    fileUploadedStatus,
    explanationText = "uploadFilePartnerExplanation",
    linkText = "uploadExplanationLinkTemplateFile",
    templateFileUrl = config.customerManagementDocumentUrl,
    showExplanationSection = true,
    fileTypes,
    fileIcon = Excel,
}) => {
    const translate = useTranslate();
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState<FileUploadStatus>(fileUploadedStatus || FileUploadStatus.INITIAL);

    const onDeleteFile = () => {
        setFile(null);
        setStatus(FileUploadStatus.INITIAL);
        onDeleteDocument?.();
    };

    useEffect(() => {
        setStatus(fileUploadedStatus || FileUploadStatus.INITIAL);
    }, [fileUploadedStatus]);

    const handleUpload = async (uploadedFile: File) => {
        try {
            setStatus(FileUploadStatus.LOADING);
            await onLoadDocument(uploadedFile);
            setFile(uploadedFile);
            setStatus(FileUploadStatus.SUCCESS);
            return "OK";
        } catch (err) {
            console.error(err);
            setStatus(FileUploadStatus.ERROR);
            return "ERROR";
        }
    };

    const renderUploadMessage = (): React.ReactNode => {
        switch (status) {
            case FileUploadStatus.LOADING:
                return (
                    <FileUploadLoading>
                        <Typography variant="body2Regular">{translate("uploadingElement")}</Typography>
                        <ProgressBarRoot value={50} />
                    </FileUploadLoading>
                );
            case FileUploadStatus.SUCCESS:
                return (
                    <FileUploadSuccess>
                        <CheckIconRoot />
                        <Typography variant="body3Strong">{translate("uploadCompleted")}</Typography>
                    </FileUploadSuccess>
                );
            case FileUploadStatus.ERROR:
                return (
                    <FileUploadError>
                        <WarningIconRoot />
                        <Typography variant="body3Strong" color="red">
                            {translate("uploadError")}
                        </Typography>
                        <Typography
                            variant="body3Strong"
                            color="red"
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            onClick={() => {
                                setStatus(FileUploadStatus.INITIAL);
                                setFile(null);
                            }}
                        >
                            {translate("retryWithAnotherFile")}
                        </Typography>
                    </FileUploadError>
                );
            default:
                return null;
        }
    };

    return (
        <Fragment>
            {showExplanationSection && (
                <ExplanationSection>
                    <TranslatedText
                        textKey={explanationText}
                        values={{
                            linkText: (
                                <a href={templateFileUrl} target="_blank" rel="noreferrer" style={{ color: "#43398E", fontWeight: 600 }}>
                                    {linkText}
                                </a>
                            ),
                        }}
                    />
                </ExplanationSection>
            )}
            <FileUploadArea
                fileUploadedStatus={status}
                uploadMessage={renderUploadMessage()}
                onUpload={handleUpload}
                icon={<img width={45} src={fileIcon} alt={translate("uploadFileText")} />}
                uploadText={translate("uploadFileText")}
                fileTypes={fileTypes}
            />
            {file && <UploadedFileItem file={file} onDelete={onDeleteFile} />}
        </Fragment>
    );
};

export default FileUploader;
